import { useState, useMemo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowUp } from 'lucide-react';
import { Footer } from '../components/layout/Footer';
import { createBubblePopSound } from '../utils/soundEffects';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

type AnimationPhase = 'appearing' | 'typing-indicator' | 'typing-text' | 'complete';

const faqData: FAQItem[] = [
  {
    question: "What is Social Pack and how does it work?",
    answer: "Social Pack is an AI-powered content tool from Throttle Labs that turns a reference headshot and a few prompts into on-brand social images and captions. Upload a photo you own rights to, pick scenes/styles, and generate content sized for major platforms.",
    category: "Platform Features"
  },
  {
    question: "Is AI image generation legal for marketing?",
    answer: "Yes—if you have rights to the inputs (headshots, brand assets) and you respect likeness/publicity, copyright, and advertising laws. Always use consented photos and avoid using third-party logos, celebrity likenesses, or trademarked IP.",
    category: "Legal & Compliance"
  },
  {
    question: "Do I own the images Social Pack generates?",
    answer: "As between you and Throttle Labs, you own the outputs to the extent permitted by law and subject to third-party rights. You are responsible for obtaining consent for anyone depicted and for how you publish the content.",
    category: "Legal & Compliance"
  },
  {
    question: "Will the AI keep my face accurate?",
    answer: "Yes—Social Pack uses a likeness lock to preserve facial features. AI is probabilistic, though, and we only guarantee up to 60% accuracy per our Terms. Always review before posting.",
    category: "Platform Features"
  },
  {
    question: "Can I use client photos in Social Pack?",
    answer: "Only with explicit, written permission. You're responsible for having the rights and consent to upload any person's image. When in doubt, don't upload.",
    category: "Legal & Compliance"
  },
  {
    question: "Does Social Pack store my headshots?",
    answer: "You can choose Ephemeral (auto-delete after generation/session) or Library (save profiles until you delete). See our Privacy Policy for details.",
    category: "Privacy & Security"
  },
  {
    question: "Which platforms do you support for export sizes?",
    answer: "We generate posts for Instagram (1:1, 4:5, 9:16), Facebook, LinkedIn, X/Twitter, YouTube Shorts, and TikTok. You can also export generic sizes.",
    category: "Platform Features"
  },
  {
    question: "What image styles can I choose?",
    answer: "Photoreal lifestyle, cinematic film-still, soft DreamWorks-style realism, or clean studio. Pick via radio buttons; the style directive is baked into the prompt.",
    category: "Platform Features"
  },
  {
    question: "How many images can I create per month?",
    answer: "Depends on your plan limits (credits). Plans show monthly caps and rollover rules in-app during checkout.",
    category: "Pricing & Plans"
  },
  {
    question: "Can I generate images with seasonal or holiday themes?",
    answer: "Yes. Social Pack includes curated seasonal, holiday, and event prompts that avoid religious or trademarked imagery while keeping your brand tasteful and relevant.",
    category: "Platform Features"
  },
  {
    question: "What are the best image sizes for Instagram?",
    answer: "Feed: 1:1 (1080×1080) or 4:5 (1080×1350). Stories/Reels: 9:16 (1080×1920). We export at high resolution and keep subject framing safe for crop.",
    category: "Social Media Best Practices"
  },
  {
    question: "Does Social Pack generate captions and hashtags?",
    answer: "Yes—caption seeds and hashtag sets can be included with each image pack, tuned for real estate and mortgage audiences.",
    category: "Platform Features"
  },
  {
    question: "What's the difference between photoreal and cinematic?",
    answer: "Photoreal mimics modern lifestyle photography with clean skin tones and natural light. Cinematic adds filmic contrast, bolder color grade, and storytelling composition.",
    category: "Platform Features"
  },
  {
    question: "Can I upload my brand colors and fonts?",
    answer: "Yes—set brand colors and tone preferences. Avoid uploading licensed fonts unless you have the rights.",
    category: "Platform Features"
  },
  {
    question: "How do I keep a consistent brand look across posts?",
    answer: "Use the same headshot profile, pick a consistent style directive, set brand colors, and reuse approved scene prompts. Maintain a simple grid pattern (e.g., education, listing, lifestyle, testimonial).",
    category: "Social Media Best Practices"
  },
  {
    question: "Will AI images hurt my authenticity?",
    answer: "Not if used thoughtfully. Pair AI visuals with real photos, client stories, and behind-the-scenes clips. Authentic copy + consistent value beats pure polish.",
    category: "Social Media Best Practices"
  },
  {
    question: "What social-media frequency do you recommend for agents?",
    answer: "Start with 3–5 posts/week plus 3–5 Stories/Reels. Batch your content monthly, then schedule with a calendar.",
    category: "Social Media Best Practices"
  },
  {
    question: "Do I need disclosures on my posts?",
    answer: "Yes, when required (e.g., #ad for endorsements, local real-estate disclosures). Follow your broker, MLS, and jurisdiction rules.",
    category: "Legal & Compliance"
  },
  {
    question: "Can I post AI images of properties I don't represent?",
    answer: "No. Don't mislead. Use generic interiors/exteriors or clearly state \"example scene\" unless you have listing rights.",
    category: "Legal & Compliance"
  },
  {
    question: "How do I avoid copyright and trademark issues?",
    answer: "Use generic signage and props, avoid brand logos and characters, and publish only assets you own or license. Social Pack's prompts are designed to be brand-safe.",
    category: "Legal & Compliance"
  },
  {
    question: "How do I get better engagement with AI images?",
    answer: "Lead with value (tips, neighborhood insights), use clear hooks in the first line, add a CTA, and keep visuals readable on mobile.",
    category: "Social Media Best Practices"
  },
  {
    question: "What's the best posting time?",
    answer: "Test your audience. As a starting point: weekdays 8–10am or 5–7pm local time; weekends mid-morning. Use insights to refine.",
    category: "Social Media Best Practices"
  },
  {
    question: "Should I boost posts or run ads?",
    answer: "Boosting is fine for quick reach, but ads with proper targeting and conversion goals are more efficient. Keep creative tightly aligned to ad copy.",
    category: "Social Media Best Practices"
  },
  {
    question: "Can AI images be used for print?",
    answer: "Yes—export high-resolution. For signage or mailers, check DPI (300) and bleed settings before sending to print.",
    category: "Platform Features"
  },
  {
    question: "What are social-media dos and don'ts for Realtors/MLOs?",
    answer: "Do: be accurate, disclose relationships, respect privacy. Don't: promise outcomes, misuse MLS photos, or show client data.",
    category: "Legal & Compliance"
  },
  {
    question: "How do I balance professional vs. casual tone?",
    answer: "Use professional for listings/closings; business casual for community, education, and behind-the-scenes. Keep your bio and highlights consistent.",
    category: "Social Media Best Practices"
  },
  {
    question: "What's a safe caption template?",
    answer: "Hook → one insight → benefit → CTA. Example: \"3 ways to prep your home this month… want the checklist? DM me 'READY'.\"",
    category: "Social Media Best Practices"
  },
  {
    question: "Can I repurpose one image across platforms?",
    answer: "Yes—resize for aspect ratio, adjust caption length and hashtags, and refresh the CTA by platform.",
    category: "Social Media Best Practices"
  },
  {
    question: "Does Social Pack work for teams and brokerages?",
    answer: "Yes—set shared brand kits and roles. Create agent profiles with individual headshots but unified style directives.",
    category: "Platform Features"
  },
  {
    question: "How do I avoid uncanny AI faces?",
    answer: "Use a high-quality reference, lock identity, choose natural lighting styles, and keep expressions simple. Avoid extreme wide angles or heavy filters.",
    category: "Platform Features"
  },
  {
    question: "How accurate is Social Pack?",
    answer: "Per our Terms, we only guarantee up to 60% accuracy to prompts/constraints. Always review and edit before publishing.",
    category: "Platform Features"
  },
  {
    question: "Can I request specific camera settings?",
    answer: "Yes—each scene includes lens, f-stop, angle, and lighting. You can edit those fields or pick from our presets.",
    category: "Platform Features"
  },
  {
    question: "What content gets the most saves and shares?",
    answer: "Local market explainers, neighborhood spotlights, step-by-step guides, and \"what to expect\" timelines outperform pure promo posts.",
    category: "Social Media Best Practices"
  },
  {
    question: "Is it okay to use humor with AI images?",
    answer: "Absolutely—keep it brand-safe, inclusive, and relevant. Avoid humor that could be misinterpreted in financial or legal contexts.",
    category: "Social Media Best Practices"
  },
  {
    question: "How do I track ROI from social content?",
    answer: "Use UTM links, lead forms, track DMs, and attribute calls/messages back to posts. Compare cost per lead vs. other channels.",
    category: "Social Media Best Practices"
  },
  {
    question: "What's the ideal monthly workflow?",
    answer: "Plan themes → batch prompts → generate packs → edit captions → schedule → measure → iterate. Social Pack is built for batching.",
    category: "Social Media Best Practices"
  },
  {
    question: "Can I edit AI images after generation?",
    answer: "Yes—crop, color grade, add overlays, and update captions. Keep brand colors consistent.",
    category: "Platform Features"
  },
  {
    question: "Do AI images impact SEO?",
    answer: "Indirectly. Images improve dwell time and CTR if paired with good on-page copy, alt text, and fast loading.",
    category: "Social Media Best Practices"
  },
  {
    question: "What should I put in alt text?",
    answer: "Describe the subject and context plainly: \"Real estate agent handing keys at sunset porch—new homeowner moment.\"",
    category: "Social Media Best Practices"
  },
  {
    question: "How do I prevent misinformation?",
    answer: "Verify details before posting, avoid guarantees, and cite sources for market stats. Keep opinions clearly labeled as such.",
    category: "Legal & Compliance"
  },
  {
    question: "What's the safest way to use client testimonials?",
    answer: "Get written permission, avoid full names unless approved, and don't share personal financial details.",
    category: "Legal & Compliance"
  },
  {
    question: "How do I choose seasonal content?",
    answer: "Use our seasonal library (month/holiday aware), keep motifs subtle, and avoid religious/political endorsements.",
    category: "Platform Features"
  },
  {
    question: "Can I schedule content directly from Social Pack?",
    answer: "You can export and schedule with your preferred tool. Native scheduling integration is on our roadmap.",
    category: "Platform Features"
  },
  {
    question: "What's better: Reels/TikTok or static posts?",
    answer: "Short video reaches more people; carousels drive saves. Mix both—educational reels + carousel checklists.",
    category: "Social Media Best Practices"
  },
  {
    question: "Does AI replace a photographer or designer?",
    answer: "No. AI accelerates content production; human oversight ensures taste, accuracy, and compliance.",
    category: "Platform Features"
  },
  {
    question: "How do I avoid repeating myself on social?",
    answer: "Rotate pillars: education, listing, lifestyle, testimonial, community, market update. Reuse ideas with new visuals or hooks.",
    category: "Social Media Best Practices"
  },
  {
    question: "What makes a great hook?",
    answer: "Be specific and benefit-led: \"3 ways to reduce closing delays this month.\" Avoid vague claims.",
    category: "Social Media Best Practices"
  },
  {
    question: "How should I use hashtags in 2025?",
    answer: "Use 3–8 relevant tags mixing broad (#RealEstate) and niche (#KingmanAZHomes). Prioritize keywords in captions over hashtag stuffing.",
    category: "Social Media Best Practices"
  },
  {
    question: "Can I use Social Pack without showing my face?",
    answer: "Yes—use generic, brand-safe scenes, product mockups, and text-graphics. Face-forward content usually performs better, though.",
    category: "Platform Features"
  },
  {
    question: "How do I contact Throttle Labs?",
    answer: "Email support@throttlelabs.ai for help, privacy@throttlelabs.ai for privacy, and legal@throttlelabs.ai for terms or compliance.",
    category: "Support"
  }
];

const categories = Array.from(new Set(faqData.map(item => item.category)));

const TypingIndicator = () => (
  <div className="flex space-x-1 py-2">
    <div className="w-2 h-2 bg-throttle-navy-900/60 rounded-full animate-typing-dot-1"></div>
    <div className="w-2 h-2 bg-throttle-navy-900/60 rounded-full animate-typing-dot-2"></div>
    <div className="w-2 h-2 bg-throttle-navy-900/60 rounded-full animate-typing-dot-3"></div>
  </div>
);

export default function FAQ() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [openFAQIndex, setOpenFAQIndex] = useState<number | null>(null);
  const [animationPhase, setAnimationPhase] = useState<AnimationPhase>('appearing');
  const [typedText, setTypedText] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);

  const typingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const filteredFAQs = useMemo(() => {
    return faqData.filter(item => {
      const matchesSearch =
        item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.answer.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'All' || item.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, selectedCategory]);

  useEffect(() => {
    audioRef.current = new Audio('/sounds/bubble-pop.mp3');
    audioRef.current.volume = 0.4;

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      if (typingTimerRef.current) {
        clearTimeout(typingTimerRef.current);
      }
    };
  }, []);

  const playBubbleSound = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {
        // If audio file fails, use Web Audio API fallback
        try {
          createBubblePopSound();
        } catch (e) {
          // Silently handle if sound can't play
        }
      });
    } else {
      // No audio file loaded, use Web Audio API
      try {
        createBubblePopSound();
      } catch (e) {
        // Silently handle if sound can't play
      }
    }
  };

  const typeText = (text: string, startIndex: number = 0) => {
    if (startIndex < text.length) {
      setTypedText(text.substring(0, startIndex + 1));
      typingTimerRef.current = setTimeout(() => {
        typeText(text, startIndex + 1);
      }, 40);
    } else {
      setAnimationPhase('complete');
      setIsAnimating(false);
    }
  };

  const handleQuestionClick = (index: number) => {
    if (isAnimating) return;

    if (openFAQIndex === index) {
      setOpenFAQIndex(null);
      setTypedText('');
      return;
    }

    setIsAnimating(true);

    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
    }

    if (openFAQIndex !== null) {
      setOpenFAQIndex(null);
      setTypedText('');

      setTimeout(() => {
        startAnswerAnimation(index);
      }, 300);
    } else {
      startAnswerAnimation(index);
    }
  };

  const startAnswerAnimation = (index: number) => {
    setOpenFAQIndex(index);
    setAnimationPhase('appearing');
    setTypedText('');

    playBubbleSound();

    setTimeout(() => {
      setAnimationPhase('typing-indicator');

      setTimeout(() => {
        setAnimationPhase('typing-text');
        const answer = filteredFAQs[index].answer;
        typeText(answer, 0);
      }, 2000);
    }, 300);
  };

  const handleScroll = () => {
    setShowScrollTop(window.scrollY > 400);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setOpenFAQIndex(null);
    setTypedText('');
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
    }
  }, [searchQuery, selectedCategory]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100">
      <style>{`
        @keyframes bubble-pop {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.05);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes typing-dot {
          0%, 60%, 100% {
            transform: translateY(0);
            opacity: 0.4;
          }
          30% {
            transform: translateY(-8px);
            opacity: 1;
          }
        }

        .animate-bubble-pop {
          animation: bubble-pop 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
        }

        .animate-typing-dot-1 {
          animation: typing-dot 1.4s infinite;
        }

        .animate-typing-dot-2 {
          animation: typing-dot 1.4s infinite 0.2s;
        }

        .animate-typing-dot-3 {
          animation: typing-dot 1.4s infinite 0.4s;
        }

        .answer-bubble-closing {
          animation: bubble-close 0.2s ease-out forwards;
        }

        @keyframes bubble-close {
          from {
            transform: scale(1);
            opacity: 1;
          }
          to {
            transform: scale(0.95);
            opacity: 0;
          }
        }
      `}</style>

      <header className="bg-gradient-to-r from-throttle-navy-900 via-throttle-navy-700 to-throttle-navy-900 shadow-lg">
        <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center space-x-3">
              <img
                src="/images/copy_of_grunge_urban_spray_music_studio_logo_(2).png"
                alt="Social Pack"
                className="h-32 sm:h-40 w-auto"
              />
            </Link>
            <div className="flex items-center gap-3 sm:gap-4">
              <Link
                to="/blog"
                className="text-white hover:text-throttle-cyan-400 font-medium transition-colors text-sm sm:text-base"
              >
                Blog
              </Link>
              <Link
                to="/login"
                className="px-4 sm:px-6 py-2 sm:py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-all duration-200 border border-white/30 hover:border-white/50 backdrop-blur text-sm sm:text-base"
              >
                Login
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12 max-w-5xl">
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-throttle-navy-900 mb-3 sm:mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
            Click on any question to see the answer. Last updated: November 4, 2025
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="relative mb-4 sm:mb-6">
            <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search questions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 sm:pl-12 pr-4 py-3 sm:py-4 border-2 border-gray-200 rounded-xl focus:border-throttle-cyan-500 focus:outline-none text-sm sm:text-base transition-colors"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory('All')}
              className={`px-3 sm:px-4 py-2 rounded-lg font-medium transition-all text-xs sm:text-sm ${
                selectedCategory === 'All'
                  ? 'bg-throttle-navy-900 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-3 sm:px-4 py-2 rounded-lg font-medium transition-all text-xs sm:text-sm ${
                  selectedCategory === category
                    ? 'bg-throttle-navy-900 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-6 sm:space-y-8 mb-12">
          {filteredFAQs.length === 0 ? (
            <div className="text-center py-12 sm:py-16">
              <p className="text-lg sm:text-xl text-gray-500">No questions found matching your search.</p>
            </div>
          ) : (
            filteredFAQs.map((faq, index) => (
              <div key={index} className="space-y-3 sm:space-y-4 animate-fadeIn">
                <div className="flex justify-start">
                  <button
                    onClick={() => handleQuestionClick(index)}
                    disabled={isAnimating}
                    className={`max-w-[85%] sm:max-w-[75%] bg-white rounded-2xl rounded-tl-sm shadow-md p-4 sm:p-5 border-l-4 border-throttle-navy-900 text-left transition-all duration-200 ${
                      isAnimating ? 'cursor-wait' : 'cursor-pointer hover:shadow-lg hover:scale-[1.02]'
                    }`}
                  >
                    <p className="text-sm sm:text-base font-semibold text-throttle-navy-900 leading-relaxed">
                      {faq.question}
                    </p>
                  </button>
                </div>

                {openFAQIndex === index && (
                  <div className="flex justify-end">
                    <div className="max-w-[85%] sm:max-w-[75%] bg-gradient-to-br from-throttle-cyan-500 to-throttle-cyan-600 rounded-2xl rounded-tr-sm shadow-md p-4 sm:p-5 animate-bubble-pop">
                      {animationPhase === 'typing-indicator' && <TypingIndicator />}

                      {(animationPhase === 'typing-text' || animationPhase === 'complete') && (
                        <p className="text-sm sm:text-base text-throttle-navy-900 leading-relaxed font-medium">
                          {typedText}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 sm:bottom-8 sm:right-8 bg-throttle-navy-900 text-white p-3 sm:p-4 rounded-full shadow-lg hover:bg-throttle-navy-700 transition-all duration-300 hover:scale-110 z-50"
          aria-label="Scroll to top"
        >
          <ArrowUp className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
      )}

      <Footer />
    </div>
  );
}
